import dayjs from 'dayjs';

import type {
  AccountRequest,
  AccountResponseItem,
} from '@/features/account/types';
import {
  ShopContractTypeEnum,
  ShopResponseItem,
  ShopShippingInformation,
} from '@/features/shop/types';

import { MATERIAL_TABLE_LABELS } from '../constants';

export enum OrganizationContractStatusEnum {
  'under-contract' = 'under-contract',
  'outside-contract' = 'outside-contract',
}

export enum OrganizationRegisterStatusEnum {
  'unregistered' = 'unregistered',
  'registered' = 'registered',
}

export enum DrinkMaterialTypeEnum {
  drink = 'drink',
  ink = 'ink',
  label = 'label',
  other = 'other',
}

export interface OrganizationsResponse {
  total_records: number;
  organizations: OrganizationResponseItem[];
}

export interface OrganizationResponse extends OrganizationResponseItem {
  login_account_id: number;
}

export interface OrganizationShopsResponse {
  total_records: number;
  is_store_registration_approved: boolean;
  shops: ShopResponseItem[];
}

export interface OrganizationResponseItem extends OrganizationRequest {
  id: number;
  is_acode_registered: boolean;
  contract_status: OrganizationContractStatusEnum;
  contract_type: ShopContractTypeEnum;
  is_store_registration_approved: boolean;
  invoice_type: InvoiceAndSaleFigureType;
}

export interface OrganizationRequest {
  name: string;
  pic_name: string;
  pic_email: string;
  is_use_wedding_store: boolean;
  sale_area_ids: number[];
  operation_start_date: dayjs.Dayjs;
  operation_end_date: dayjs.Dayjs;
  contract_type: ShopContractTypeEnum;
  contract_type_editable: boolean;
}

export interface OrganizationFiltersFormValues {
  id: number | undefined;
  contract_status?: OrganizationContractStatusEnum[];
  acode_status?: OrganizationRegisterStatusEnum[];
  beneficiary_account_status: OrganizationRegisterStatusEnum[];
}

export interface OrganizationAccountsRequest {
  accounts: AccountRequest[];
}

export interface OrganizationOfMeResponse {
  organizations: OrganizationResponseItem[];
  shops: Record<string, ShopResponseItem[]>;
}

export interface MaterialsResponseItem {
  id: number;
  shop_name: string;
  created_at: string;
}

export interface MaterialsResponse {
  total_records: number;
  is_store_registration_approved: boolean;
  drink_and_material_orders: MaterialsResponseItem[];
}

export interface PrinterAndFixturesResponse {
  total_records: number;
  is_store_registration_approved: boolean;
  printer_and_fixtures: MaterialsResponseItem[];
}

export interface DrinkMaterialItemsResponse {
  id: number;
  name: string;
  packaging_unit: string;
  price: number;
  unit: string;
  quantity: number | undefined;
  type: keyof typeof MATERIAL_TABLE_LABELS;
}

type DrinkMaterialItem = Record<'id' | 'quantity' | 'price', number>;

export interface DrinkMaterialsFormValues {
  created_at?: string;
  shop_id: number;
  assigned_sale_name: string;
  is_first_order: boolean;
  remark?: string;
  drink: DrinkMaterialItem[];
  ink: DrinkMaterialItem[];
  label: DrinkMaterialItem[];
  other: DrinkMaterialItem[];
  shipping_information: ShopShippingInformation;
}

export interface DrinkMaterialsRequest {
  remark?: string;
  shipping_information: ShopShippingInformation;
  details: DrinkMaterialItem[];
}

export interface DrinkMaterialResponse {
  id: number;
  shop_id: number;
  total: number;
  remark: string;
  details: DrinkMaterialItemsResponse[];
}

export interface PrinterAndFixtureRequest {
  shop_id: string;
  shop_name: string;
  fcode: string;
  expected_monthly_sales: number;
  desired_receipt_date: dayjs.Dayjs;
  delivery_time: string;
  printer_quantity: number;
  remark: string;
  acrylic_fixture_quantity: number;
  card_rack_half_fixture: number;
  card_rack_for_order_fixture: number;
  card_leaflet_fixture_quantity: number;
  card_rack_full_fixture_quantity: number;
  created_at: string;
}

export interface BillingInformationRequest {
  organization_id: string;
  organization_name: string;
  invoice_type: InvoiceAndSaleFigureType;
  sale_figure_type: InvoiceAndSaleFigureType;
  is_enter_sales: boolean;
  accounts: AccountResponseItem[];
  receive_sale_figure_email_account_ids: number[];
  invoice_managements: InvoicesItem[];
  invoice_registration: string;
}

export interface InvoicesItem {
  shop_id: number;
  shop_name: string;
  contact_name: string;
  company_name?: string;
  zipcode: string;
  address: string;
  email_address?: string;
  acode?: string;
  beneficiary_account?: string;
  phone_number: string;
}

export type InvoiceAndSaleFigureType =
  | 'report-for-chain'
  | 'report-for-each-store';

export interface BillingUpdateHistoryItem {
  id: number;
  created_at: string;
  created_by: string;
  file_name: string;
  is_success: boolean;
  upload_by: { admin_id: number; email: string; name: string };
}

export interface BillingHistoryResponse {
  histories: BillingUpdateHistoryItem[];
}

export interface BillingDownloadResponse {
  url: string;
}

export interface BillingUpdateFormValues {
  files: { originFileObj: File }[];
}

export interface BillingUpdateRequest {
  image_path: string;
  file_name: string;
}
